import { useState } from 'react';

let timerId: NodeJS.Timeout;
let inProgress: boolean;
let request: any;
let warningRequest: any;
let warningInfoRequest: any;
let amlRequest: any;
let statusRequest: any;
let signatureRequest: any;
let chatsRequest: any;

const useRetry = ({
  getTransactionFunction,
  getTransactionWarningsFunction,
  getAmlFunction,
  getStatusesFunction,
  getSignaturesFunction,
  getTransactionsChatsFunction,
  getTransactionWarningsInfoFunction,
  delay = 5000,
  id,
  isRetry,
  statusPermission,
  chatPermission,
  statusUrl,
  warningsInfoPermission,
}: {
  getTransactionFunction: (data: any) => void;
  getAmlFunction?: (data: any) => void;
  getStatusesFunction?: (data: any) => void;
  getTransactionWarningsFunction?: (data: any) => void;
  getSignaturesFunction?: (data: any) => void;
  getTransactionsChatsFunction?: (data: any) => void;
  getTransactionWarningsInfoFunction?: (data: any) => void;
  id: string;
  statusPermission?: boolean;
  chatPermission?: boolean;
  warningsInfoPermission?: boolean;
  statusUrl?: string;
  delay?: number;
  isRetry?: boolean;
}) => {
  const [internalError, setInternalError] = useState('');

  const getData = async (transactionId: string) => {
    setInternalError('');
    inProgress = true;

    request = getTransactionFunction(transactionId);

    const response = await request;

    if (response?.data?.data) {
      inProgress = false;
    } else if (response.error) {
      inProgress = false;
      request?.abort();

      if (response.error.status === 400) {
        if (Array?.isArray(response?.error?.data?.message)) {
          if (response?.error?.data?.message[0]) {
            setInternalError(response?.error?.data?.message[0].value);
          }
        } else if (typeof response?.error?.data?.message === 'string') {
          setInternalError(response?.error?.data?.message);
        } else {
          setInternalError('Internal Server Error. Try Again');
        }
      } else {
        setInternalError('Internal Server Error. Try Again');
      }
    }

    if (response?.data?.data) {
      if (getAmlFunction) {
        amlRequest = getAmlFunction(transactionId);
      }

      if (getSignaturesFunction) {
        signatureRequest = getSignaturesFunction({
          id: transactionId,
          url: statusUrl,
        });
      }

      if (getTransactionsChatsFunction && chatPermission) {
        chatsRequest = getTransactionsChatsFunction(transactionId);
      }

      if (statusPermission && getStatusesFunction && response?.data?.data) {
        statusRequest = getStatusesFunction({
          id: transactionId,
          url: statusUrl,
          baseellaStatusFrom:
            response?.data?.data?.latestQueuedStatuses?.baseellaStatus ||
            response?.data?.data?.baseellaStatus,
        });
      }

      if (getTransactionWarningsFunction) {
        warningRequest = getTransactionWarningsFunction(transactionId);
      }

      if (getTransactionWarningsInfoFunction && warningsInfoPermission) {
        warningInfoRequest = getTransactionWarningsInfoFunction(transactionId);
      }
    }
  };

  const onClearTimer = async (leave?: boolean) => {
    inProgress = false;

    if (leave) {
      request?.abort();

      if (amlRequest) {
        amlRequest?.abort();
      }

      if (statusRequest) {
        statusRequest?.abort();
      }

      if (warningRequest) {
        warningRequest?.abort();
      }

      if (warningInfoRequest) {
        warningInfoRequest?.abort();
      }

      if (signatureRequest) {
        signatureRequest?.abort();
      }

      if (chatsRequest) {
        chatsRequest?.abort();
      }
    }

    await clearTimeout(timerId);
  };

  const shedule = async (transactionId: string, isFirst?: boolean) => {
    await onClearTimer();

    if (!inProgress) {
      if (isFirst) {
        await getData(transactionId);
      }

      timerId = setTimeout(async () => {
        await getData(transactionId);
        await shedule(transactionId);
      }, delay);
    }
  };

  const getTransaction = async () => {
    await onClearTimer();

    if (isRetry) {
      await shedule(id, true);
    } else {
      await getData(id);
    }
  };

  return { getTransaction, onClearTimer, internalError };
};

export default useRetry;
